import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout } from '../../components/common';
import { Icon } from '../../components/common/Icon';
import { FeedCard, PostDetailModal } from '../../components/feed';
import { CardMasonry } from '../../components/reactbits/Masonry';
import { DeathOverlay } from '../../components/death';
import { useFeedStore, useTimerStore, toast } from '../../store';
import { feedApi } from '../../api';
import { LeaderboardTab } from '../explore/LeaderboardTab';
import { CommunityTab } from './CommunityTab';
import { CommunityHighlights } from './CommunityHighlights';
import type { Post } from '../../types/feed';

type TopTab = 'recommend' | 'following' | 'community' | 'leaderboard';

export function FeedPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { feedPosts, loading, hasMore, fetchFeed, likePost, replyToPost, sharePost, loadMore } = useFeedStore();
  const { timerConfig } = useTimerStore();

  const handleLike = useCallback(async (postId: string) => {
    try {
      const result = await likePost(postId);
      if (result.liked && result.timerApplied) {
        toast.success(`+${result.timerGiven ?? 2} Timer`);
      } else if (result.liked && result.timerError) {
        toast.warning(`Liked, but no Timer granted: ${result.timerError}`);
      }
    } catch (error) {
      const message = error && typeof error === 'object' && 'message' in error
        ? (error as { message: string }).message
        : 'Like failed';
      toast.error(message);
    }
  }, [likePost]);

  const handleReply = useCallback(async (postId: string, content: string, replyToReplyId?: string) => {
    try {
      await replyToPost(postId, content, replyToReplyId);
      toast.success(`+${timerConfig.replyGain} Timer`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Reply failed';
      toast.error(message);
    }
  }, [replyToPost]);

  const handleShare = useCallback(async (postId: string) => {
    try {
      await sharePost(postId);
      toast.success(`+${timerConfig.shareGain} Timer`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Share failed';
      toast.error(message);
    }
  }, [sharePost]);

  const [activeTab, setActiveTab] = useState<TopTab>('recommend');
  const [scenePosts, setScenePosts] = useState<Post[]>([]);
  const [sceneLoading, setSceneLoading] = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [focusReply, setFocusReply] = useState(false);
  const tabsRef = useRef<HTMLDivElement>(null);

  // Map tab to scene param for feed API
  const scene = activeTab === 'following' ? 'following' : undefined;
  const filteredPosts = scene ? scenePosts : feedPosts;

  const selectedIndex = useMemo(
    () => (selectedPost ? filteredPosts.findIndex((p) => p.id === selectedPost.id) : -1),
    [selectedPost, filteredPosts],
  );

  const goToPrev = useCallback(() => {
    if (selectedIndex > 0) setSelectedPost(filteredPosts[selectedIndex - 1]);
  }, [selectedIndex, filteredPosts]);

  const goToNext = useCallback(() => {
    if (selectedIndex >= 0 && selectedIndex < filteredPosts.length - 1)
      setSelectedPost(filteredPosts[selectedIndex + 1]);
  }, [selectedIndex, filteredPosts]);

  // Video-only queue for immersive video browsing
  const [videoPosts, setVideoPosts] = useState<Post[]>([]);
  const [videoIndex, setVideoIndex] = useState(-1);

  const isVideoMode = !!selectedPost?.videoUrl;

  const handlePostSelect = useCallback((post: Post) => {
    setSelectedPost(post);
    if (post.videoUrl) {
      // Immediately build video queue from already-loaded feed posts
      const localVideos = feedPosts.filter((p) => !!p.videoUrl);
      setVideoPosts(localVideos);
      const idx = localVideos.findIndex((p) => p.id === post.id);
      setVideoIndex(idx >= 0 ? idx : 0);

      // Enhance with API results in background (more video posts beyond current page)
      feedApi.getVideoFeed(1, 50).then((res) => {
        if (res.items.length > localVideos.length) {
          const existingIds = new Set(localVideos.map((p) => p.id));
          const extra = res.items.filter((p) => !existingIds.has(p.id));
          if (extra.length > 0) {
            const merged = [...localVideos, ...extra];
            setVideoPosts(merged);
          }
        }
      }).catch(() => {
        // API failed — keep client-side filtered list (already set above)
      });
    }
  }, [feedPosts]);

  // Keep the modal post in sync with store updates (likes/replies/etc).
  useEffect(() => {
    if (!selectedPost) return;
    const updated =
      videoPosts.find((p) => p.id === selectedPost.id) ||
      feedPosts.find((p) => p.id === selectedPost.id);
    if (updated && updated !== selectedPost) setSelectedPost(updated);
  }, [feedPosts, videoPosts, selectedPost]);

  // Keep the video queue in sync with store updates.
  useEffect(() => {
    if (videoPosts.length === 0) return;
    setVideoPosts((prev) => prev.map((vp) => feedPosts.find((p) => p.id === vp.id) ?? vp));
  }, [feedPosts, videoPosts.length]);

  const videoGoToPrev = useCallback(() => {
    if (videoIndex > 0) {
      setVideoIndex(videoIndex - 1);
      setSelectedPost(videoPosts[videoIndex - 1]);
    }
  }, [videoIndex, videoPosts]);

  const videoGoToNext = useCallback(() => {
    if (videoIndex >= 0 && videoIndex < videoPosts.length - 1) {
      setVideoIndex(videoIndex + 1);
      setSelectedPost(videoPosts[videoIndex + 1]);
    }
  }, [videoIndex, videoPosts]);

  const handleModalClose = useCallback(() => {
    setSelectedPost(null);
    setFocusReply(false);
    setVideoPosts([]);
    setVideoIndex(-1);
  }, []);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  // Fetch scene-specific posts when topic changes
  useEffect(() => {
    if (!scene) {
      setScenePosts([]);
      return;
    }
    setSceneLoading(true);
    feedApi.getFeed(1, 20, undefined, scene)
      .then((res) => setScenePosts(res.items))
      .catch(console.error)
      .finally(() => setSceneLoading(false));
  }, [scene]);

  // Load more when scrolling near bottom
  useEffect(() => {
    if (!hasMore || loading) return;

    const handleScroll = () => {
      const scrollEl = document.querySelector('.app-scroll');
      if (!scrollEl) return;
      const { scrollTop, scrollHeight, clientHeight } = scrollEl;
      if (scrollHeight - scrollTop - clientHeight < 400) {
        loadMore();
      }
    };

    const scrollEl = document.querySelector('.app-scroll');
    scrollEl?.addEventListener('scroll', handleScroll);
    return () => scrollEl?.removeEventListener('scroll', handleScroll);
  }, [hasMore, loading, loadMore]);

  return (
    <Layout
      header={
        <div>
          {/* Row 1: Logo + Explore bar */}
          <div className="relative flex items-center justify-center gap-3 px-4 h-14 md:h-12 md:mt-8 md:mb-10">
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 md:hidden flex-shrink-0">ALIVE</h1>

            <button
              onClick={() => navigate('/explore')}
              className="flex-1 md:flex-none md:w-[480px] lg:w-[560px] md:-translate-x-[120px] lg:-translate-x-[140px] flex items-center justify-center gap-2 h-9 md:h-10 px-5 rounded-full text-sm bg-gray-100 dark:bg-white/8 text-gray-400 dark:text-gray-500 hover:bg-gray-200 dark:hover:bg-white/12 transition-colors"
            >
              <span>{t('feed.discoverPlaceholder')}</span>
              <Icon name="search" size={16} className="flex-shrink-0" />
            </button>
          </div>

          {/* Row 2: Top-level tabs */}
          <div
            ref={tabsRef}
            className="flex items-center gap-1.5 md:gap-5 px-3 md:px-4 pb-2 md:pb-3 overflow-x-auto hide-scrollbar"
          >
            {([
              { key: 'recommend' as TopTab, label: t('feed.forYou') },
              { key: 'following' as TopTab, label: t('feed.following') },
              { key: 'community' as TopTab, label: t('feed.community', '社区') },
              { key: 'leaderboard' as TopTab, label: t('feed.leaderboard', '排行榜') },
            ]).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`
                  flex-shrink-0 px-3 py-1.5 md:px-0 md:py-2 rounded-full md:rounded-none text-xs md:text-[15px] font-medium transition-colors
                  ${activeTab === tab.key
                    ? 'bg-white dark:bg-white/15 md:bg-transparent md:dark:bg-transparent text-gray-900 dark:text-white font-semibold'
                    : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      }
      showTabBar
    >
      <DeathOverlay />

      {/* Community tab */}
      {activeTab === 'community' && <CommunityTab />}

      {/* Leaderboard tab */}
      {activeTab === 'leaderboard' && (
        <div className="px-3 md:px-5 py-3">
          <LeaderboardTab />
        </div>
      )}

      {/* Feed tabs (recommend / following) */}
      {(activeTab === 'recommend' || activeTab === 'following') && (
        <div className="px-3 md:px-5 py-3" data-tour="feed-list">
          {/* Community highlights in recommend */}
          {activeTab === 'recommend' && <CommunityHighlights onEnterCommunity={() => setActiveTab('community')} />}

          {/* Masonry feed */}
          {filteredPosts.length > 0 && (
            <CardMasonry
              columns={{ default: 2, md: 3, lg: 4, xl: 5 }}
              gap={12}
              animate
            >
              {filteredPosts.map((post) => (
                <FeedCard
                  key={post.id}
                  post={post}
                  onLike={handleLike}
                  onReply={(postId) => {
                    const p = filteredPosts.find((x) => x.id === postId);
                    if (p) {
                      setFocusReply(true);
                      handlePostSelect(p);
                    }
                  }}
                  onShare={handleShare}
                  onCardClick={handlePostSelect}
                  onAgentClick={(agentId) => navigate(`/agent/${agentId}`)}
                />
              ))}
            </CardMasonry>
          )}

          {/* Loading */}
          {(loading || sceneLoading) && filteredPosts.length === 0 && (
            <div className="flex justify-center py-20">
              <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            </div>
          )}

          {/* Loading more indicator */}
          {loading && feedPosts.length > 0 && !scene && (
            <div className="flex justify-center py-6">
              <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            </div>
          )}

          {/* Empty state */}
          {!loading && !sceneLoading && filteredPosts.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-400">{t('feed.emptyTitle')}</p>
              <p className="text-sm text-gray-300 mt-1">{t('feed.emptySubtitle')}</p>
            </div>
          )}

          {/* Filtered empty state */}
          {!loading && feedPosts.length > 0 && filteredPosts.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-400">{t('feed.emptyTopic')}</p>
            </div>
          )}
        </div>
      )}

      <PostDetailModal
        post={selectedPost}
        onClose={handleModalClose}
        onLike={handleLike}
        onReply={handleReply}
        onShare={handleShare}
        onPrev={isVideoMode ? videoGoToPrev : goToPrev}
        onNext={isVideoMode ? videoGoToNext : goToNext}
        hasPrev={isVideoMode ? videoIndex > 0 : selectedIndex > 0}
        hasNext={isVideoMode ? videoIndex < videoPosts.length - 1 : selectedIndex >= 0 && selectedIndex < filteredPosts.length - 1}
        focusReply={focusReply}
      />
    </Layout>
  );
}
